import { useEffect, useState } from "react";
import api, { formatApiError } from "../../api";
import { useAuth } from "../../contexts/AuthContext";
import { Coins, RefreshCw, Sparkles, TrendingUp, DollarSign } from "lucide-react";

const KIND_LABELS = {
  frame_analyse: { label: "Webcam frame · Gemini Vision", icon: "📷" },
  id_verify: { label: "ID verification · Gemini Vision", icon: "🪪" },
  integrity_verdict: { label: "Integrity verdict · Claude", icon: "🛡" },
  score_open_case_analysis: { label: "Case Analysis · Claude scoring", icon: "📝" },
  score_open_short_written: { label: "Short Written · Claude scoring", icon: "✍️" },
  score_open_open: { label: "Open response · Claude scoring", icon: "✍️" },
  roleplay_turn: { label: "Role-play turn · Claude", icon: "🎭" },
};

const fmtNumber = (n) => Number(n).toLocaleString("en-IN");
const fmtUsd = (n) => `$${Number(n).toFixed(4)}`;
const fmtInr = (n) => `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

export default function TokenCostCenter() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [seeding, setSeeding] = useState(false);
  const [seedResult, setSeedResult] = useState(null);

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const r = await api.get("/admin/token-cost");
      setData(r.data);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const reseed = async () => {
    if (!window.confirm("Re-seed the 40 consultative-selling questions? Wipes prior PoC questions and recreates the bank. Idempotent.")) return;
    setSeeding(true); setErr("");
    try {
      const r = await api.post("/admin/demo/seed-consultative-qs");
      setSeedResult(r.data);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setSeeding(false); }
  };

  if (!data && loading) return <div className="text-center text-[#878787] py-20 text-sm">Loading token-cost telemetry…</div>;
  if (err && !data) return <div className="bg-[#FEE2E2] border border-[#FCA5A5] text-[#991B1B] text-sm px-4 py-3">{err}</div>;
  if (!data) return null;

  const totalIn = data.total.input_tokens;
  const totalOut = data.total.output_tokens;
  const totalCalls = data.total.calls;
  const completed = data.completed_attempts;
  const proj = data.projections;

  const sortedKinds = Object.entries(data.by_kind).sort((a, b) => b[1].cost_usd - a[1].cost_usd);

  return (
    <div className="space-y-8 fade-in" data-testid="token-cost-center">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline mb-2 !text-[#EE2737]">VI SALES CERTIFICATION · COST PROJECTION</div>
          <h1 className="font-heading font-black text-4xl tracking-tighter">Token Cost Calculator</h1>
          <p className="text-[#525252] text-sm mt-2 max-w-3xl leading-relaxed">
            Live measurement of every LLM call in the pipeline — proctoring frames (Gemini Vision), ID verification (Gemini + GPT-4o fallback), MCQ / Sales Pitch / Role Play generation and scoring (Claude Sonnet + Haiku, OpenAI Whisper STT, OpenAI TTS), and the AI Executive Brief. Each call is logged with model, tokens, and list-price cost in USD and INR. <b>Use this to size the 150-participant projection.</b>
          </p>
        </div>
        <button onClick={load} disabled={loading} className="btn btn-secondary" data-testid="reload-cost">
          <RefreshCw size={14} /> {loading ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {/* Headline tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile icon={<TrendingUp size={16} className="text-[#EE2737]" />} label="Candidates with AI calls" value={fmtNumber(data.unique_users || 0)} sub={`${totalCalls} LLM calls · ${completed} proctored attempts`} />
        <Tile icon={<Coins size={16} className="text-[#EE2737]" />} label="Avg cost / candidate" value={fmtInr(data.avg_cost_per_user_inr || data.avg_cost_per_attempt_inr)} sub={fmtUsd(data.avg_cost_per_user_usd || data.avg_cost_per_attempt_usd)} primary />
        <Tile icon={<Sparkles size={16} className="text-[#EE2737]" />} label="Total tokens consumed" value={fmtNumber(totalIn + totalOut)} sub={`${fmtNumber(totalIn)} in · ${fmtNumber(totalOut)} out`} />
        <Tile icon={<DollarSign size={16} className="text-[#EE2737]" />} label="Total cost · USD" value={fmtUsd(data.total.cost_usd)} sub={fmtInr(data.total.cost_inr)} />
      </div>

      {/* Cohort projections */}
      <div className="card p-6">
        <div className="overline mb-1">PROJECTED COST · NSA + CSA POC</div>
        <h3 className="font-heading font-bold text-xl mb-4">If you scale to N participants at the current average</h3>
        <table className="data-table">
          <thead>
            <tr>
              <th>Cohort size</th>
              <th>Use case</th>
              <th className="text-right">Cost · USD</th>
              <th className="text-right">Cost · INR</th>
              <th>If using Emergent Universal Key</th>
            </tr>
          </thead>
          <tbody>
            {proj.map((p) => (
              <tr key={p.attempts}>
                <td className="font-bold text-lg">{p.attempts}</td>
                <td className="text-[12.5px] text-[#525252]">
                  {p.attempts === 1 ? "Single attempt" :
                   p.attempts === 30 ? "Leadership cohort (~30 leaders)" :
                   p.attempts === 150 ? "IC cohort (~150 reps)" :
                   p.attempts === 180 ? "Full NSA+CSA programme" :
                   p.attempts === 320 ? "Full VI sales org" : ""}
                </td>
                <td className="text-right tabular-nums font-bold">{fmtUsd(p.cost_usd)}</td>
                <td className="text-right tabular-nums font-bold">{fmtInr(p.cost_inr)}</td>
                <td className="text-[12px] text-[#10B981] font-bold">₹0 to you · bundled</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="text-[11px] text-[#878787] mt-3 leading-relaxed">{data.pricing_note} · FX: 1 USD = ₹{data.fx_rate}</p>
      </div>

      {/* Breakdown by call kind */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card p-6">
          <div className="overline mb-1">BREAKDOWN BY CALL TYPE</div>
          <h3 className="font-heading font-bold text-lg mb-4">Where does the cost go?</h3>
          {sortedKinds.length === 0 ? (
            <p className="text-[#878787] text-sm">No LLM calls logged yet. Take an attempt (or one role-play) to populate this view.</p>
          ) : (
            <table className="data-table">
              <thead>
                <tr><th>Call type</th><th className="text-right">Calls</th><th className="text-right">Tokens</th><th className="text-right">Cost ₹</th></tr>
              </thead>
              <tbody>
                {sortedKinds.map(([kind, v]) => {
                  const k = KIND_LABELS[kind] || { label: kind, icon: "•" };
                  return (
                    <tr key={kind}>
                      <td>{k.icon} {k.label}</td>
                      <td className="text-right tabular-nums">{v.calls}</td>
                      <td className="text-right tabular-nums text-[12px] text-[#525252]">{fmtNumber(v.input_tokens + v.output_tokens)}</td>
                      <td className="text-right tabular-nums font-bold">{fmtInr(v.cost_inr)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* By model */}
        <div className="card p-6">
          <div className="overline mb-1">BREAKDOWN BY MODEL</div>
          <h3 className="font-heading font-bold text-lg mb-4">Provider split</h3>
          {Object.keys(data.by_model).length === 0 ? (
            <p className="text-[#878787] text-sm">No model usage yet.</p>
          ) : (
            <table className="data-table">
              <thead>
                <tr><th>Model</th><th className="text-right">Calls</th><th className="text-right">Tokens</th><th className="text-right">Cost ₹</th></tr>
              </thead>
              <tbody>
                {Object.entries(data.by_model).map(([m, v]) => (
                  <tr key={m}>
                    <td className="font-mono text-[12px]">{m}</td>
                    <td className="text-right tabular-nums">{v.calls}</td>
                    <td className="text-right tabular-nums text-[12px] text-[#525252]">{fmtNumber(v.input_tokens + v.output_tokens)}</td>
                    <td className="text-right tabular-nums font-bold">{fmtInr(v.cost_inr)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Per-user drilldown — most useful for the pilot, since one candidate = one full cert run */}
      {Object.keys(data.by_user || {}).length > 0 && (
        <div className="card p-6" data-testid="token-cost-per-user">
          <div className="overline mb-1">PER-CANDIDATE</div>
          <h3 className="font-heading font-bold text-lg mb-1">
            {data.unique_users || 0} candidate(s) with logged AI calls
          </h3>
          <p className="text-[12px] text-[#525252] mb-4">
            One row per unique user_id. Use this to budget by candidate — the 150-participant projection uses the average from this table when available.
          </p>
          <table className="data-table">
            <thead>
              <tr>
                <th>Candidate</th>
                <th className="text-right">Calls</th>
                <th className="text-right">Input tokens</th>
                <th className="text-right">Output tokens</th>
                <th className="text-right">Cost ₹</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(data.by_user)
                .sort((a, b) => (b[1].cost_inr || 0) - (a[1].cost_inr || 0))
                .map(([uid, v]) => (
                <tr key={uid}>
                  <td className="text-[12px]">
                    <div className="font-bold text-[#0A0A0A]">{v.name || uid}</div>
                    <div className="text-[10px] text-[#878787]">{v.email || uid}</div>
                  </td>
                  <td className="text-right tabular-nums">{v.calls}</td>
                  <td className="text-right tabular-nums">{fmtNumber(v.input_tokens)}</td>
                  <td className="text-right tabular-nums">{fmtNumber(v.output_tokens)}</td>
                  <td className="text-right tabular-nums font-bold">{fmtInr(v.cost_inr)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Per-attempt drilldown */}
      {Object.keys(data.by_assignment).length > 0 && (
        <div className="card p-6">
          <div className="overline mb-1">PER-ATTEMPT</div>
          <h3 className="font-heading font-bold text-lg mb-4">{Object.keys(data.by_assignment).length} attempt(s) logged</h3>
          <table className="data-table">
            <thead>
              <tr><th>Assignment ID</th><th className="text-right">Calls</th><th className="text-right">Input tokens</th><th className="text-right">Output tokens</th><th className="text-right">Cost ₹</th></tr>
            </thead>
            <tbody>
              {Object.entries(data.by_assignment).map(([aid, v]) => (
                <tr key={aid}>
                  <td className="font-mono text-[11px]">{aid}</td>
                  <td className="text-right tabular-nums">{v.calls}</td>
                  <td className="text-right tabular-nums">{fmtNumber(v.input_tokens)}</td>
                  <td className="text-right tabular-nums">{fmtNumber(v.output_tokens)}</td>
                  <td className="text-right tabular-nums font-bold">{fmtInr(v.cost_inr)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Reseed PoC questions */}
      {isAdmin && (
        <div className="card p-5 border-l-4 !border-[#0A0A0A]">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="overline">POC QUESTION BANK</div>
              <h3 className="font-heading font-bold text-lg mt-1">40 consultative-selling questions · IC cohort</h3>
              <p className="text-[12.5px] text-[#525252] mt-1 max-w-2xl leading-relaxed">
                12 Scenario MCQs · 10 Situational MCQs · 6 Role-Based Decisions · 7 Case Analyses · 5 Short Written.{" "}
                Tagged <code>cohort=IC, poc=consultative-selling</code>. Idempotent re-seed below — useful if you want a clean baseline before another attempt.
              </p>
            </div>
            <button onClick={reseed} disabled={seeding} className="btn btn-secondary" style={{ background: "#0A0A0A", color: "white", borderColor: "#0A0A0A" }} data-testid="reseed-poc-questions">
              <Sparkles size={14} /> {seeding ? "Seeding…" : "Re-seed PoC bank"}
            </button>
          </div>
          {seedResult && (
            <div className="bg-[#D1FAE5] border-l-4 border-[#10B981] text-[#065F46] px-4 py-3 mt-4 text-sm">
              ✔ Seeded <b>{seedResult.total}</b> questions for cohort <b>{seedResult.cohort}</b> ({Object.entries(seedResult.counts).map(([k, v]) => `${v} ${k}`).join(" · ")})
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Tile({ icon, label, value, sub, primary }) {
  return (
    <div className={`p-4 border ${primary ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-[#E5E7EB] bg-white"}`}>
      <div className="flex items-center gap-2 mb-1">{icon}<div className="overline" style={primary ? { color: "#EE2737" } : {}}>{label}</div></div>
      <div className="font-heading font-black text-3xl">{value}</div>
      <div className={`text-[11px] mt-0.5 ${primary ? "text-white/60" : "text-[#878787]"}`}>{sub}</div>
    </div>
  );
}
